import { apiRequest } from './api'
import { Booking, BookingStatus } from '../types'

interface BookingsResponse {
  data: Booking[]
  total: number
  page: number
  limit: number
  totalPages: number
}

interface BookingStats {
  totalBookings: number
  pendingBookings: number
  confirmedBookings: number
  inProgressBookings: number
  completedBookings: number
}

export const bookingsService = {
  // Get all bookings
  async getBookings(params?: {
    caseId?: string
    status?: BookingStatus
    page?: number
    limit?: number
  }): Promise<BookingsResponse> {
    const response = await apiRequest.get<BookingsResponse>('/bookings', params)
    return response.data
  },

  // Get booking by ID
  async getBookingById(id: string): Promise<Booking> {
    const response = await apiRequest.get<Booking>(`/bookings/${id}`)
    return response.data
  },

  // Create new booking
  async createBooking(bookingData: any): Promise<Booking> {
    const response = await apiRequest.post<Booking>('/bookings', bookingData)
    return response.data
  },

  // Update booking
  async updateBooking(id: string, bookingData: any): Promise<Booking> {
    const response = await apiRequest.patch<Booking>(`/bookings/${id}`, bookingData)
    return response.data
  },

  // Confirm booking
  async confirmBooking(id: string): Promise<Booking> {
    const response = await apiRequest.patch<Booking>(`/bookings/${id}/confirm`)
    return response.data
  },

  // Start booking (mark as in progress)
  async startBooking(id: string): Promise<Booking> {
    const response = await apiRequest.patch<Booking>(`/bookings/${id}/start`)
    return response.data
  },

  // Complete booking
  async completeBooking(id: string): Promise<Booking> {
    const response = await apiRequest.patch<Booking>(`/bookings/${id}/complete`)
    return response.data
  },

  // Cancel booking
  async cancelBooking(id: string, reason?: string): Promise<Booking> {
    const response = await apiRequest.patch<Booking>(`/bookings/${id}/cancel`, { reason })
    return response.data
  },

  // Get provider bookings
  async getProviderBookings(providerId: string, params?: {
    status?: BookingStatus
    date?: string
  }): Promise<Booking[]> {
    const response = await apiRequest.get<Booking[]>(`/bookings/provider/${providerId}`, params)
    return response.data
  },

  // Get booking statistics
  async getBookingStats(): Promise<BookingStats> {
    const response = await apiRequest.get<BookingStats>('/bookings/stats')
    return response.data
  },

  // Admin: Get all pending bookings
  async getPendingBookings(page = 1, limit = 10): Promise<BookingsResponse> {
    const response = await apiRequest.get<BookingsResponse>(
      `/bookings/admin/pending?page=${page}&limit=${limit}`
    )
    return response.data
  },

  // Admin: Approve a booking
  async approveBooking(bookingId: string, notes?: string): Promise<Booking> {
    const response = await apiRequest.patch<Booking>(`/bookings/${bookingId}/approve`, {
      notes
    })
    return response.data
  },

  // Admin: Reject a booking
  async rejectBooking(bookingId: string, reason: string): Promise<Booking> {
    const response = await apiRequest.patch<Booking>(`/bookings/${bookingId}/reject`, {
      reason
    })
    return response.data
  },
}